/**
 * A modalidade da operação pelo painel (E222.F1, owner, 04/11, GT-2026-0091): quando o material não
 * a identifica (nem termo de autorização do INSS, nem cédula que nomeie o benefício ou outra folha),
 * o perito a informa com fundamento. A revisão grava no protocolo do caso, as peças são conferidas
 * de novo e o exame se refaz dizendo que a modalidade foi informada pelo assistente técnico.
 *
 * Abre pela trava do perfil normativo no documento da mesa (`TravaDoPerfil`), com a modalidade já
 * informada escolhida. Outra folha não tem perfil no catálogo: o painel avisa antes de salvar.
 */
import { useState } from 'react';

import { Field, Select } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import { CHAVE_DA_MODALIDADE, MODALIDADES_INFORMADAS, vigenteEntre } from '@/lib/revisaoDoDado';

import { Fundamento, faltaNoFundamento } from './Fundamento';
import { BotaoSalvar, Falta, useGravacao } from './Gravacao';
import { gravarRevisao } from './gravarRevisao';
import type { PropsDoConteudo } from './tipos';

const t = ptBR.painelDoDado.modalidade;
const falta = ptBR.painelDoDado.falta;

export function ModalidadeNoPainel(props: PropsDoConteudo) {
  const { pedido, acoes, onMudou } = props;
  const [modalidade, setModalidade] = useState(() =>
    vigenteEntre(MODALIDADES_INFORMADAS, pedido.origem.valor),
  );
  const [fundamento, setFundamento] = useState('');
  const [oQueFalta, setOQueFalta] = useState<string | null>(null);
  const gravacao = useGravacao(
    () =>
      gravarRevisao(props, {
        alvo: 'modalidade',
        chave: CHAVE_DA_MODALIDADE,
        valor: modalidade,
        fundamento: fundamento.trim(),
      }),
    () => {
      acoes.salvo();
      onMudou();
    },
  );
  function salvar(): void {
    const pendente = modalidade === '' ? falta.modalidade : faltaNoFundamento(fundamento);
    setOQueFalta(pendente);
    if (pendente === null) void gravacao.salvar();
  }
  return (
    <div className="space-y-sc-3" data-testid="painel-modalidade">
      <p className="text-body-sm text-fg-muted">{t.texto}</p>
      <Field label={t.rotulo} htmlFor="painel-modalidade-valor">
        <Select
          id="painel-modalidade-valor"
          value={modalidade}
          onChange={(e) => setModalidade(vigenteEntre(MODALIDADES_INFORMADAS, e.target.value))}
          data-testid="painel-modalidade-valor"
        >
          <option value="" disabled>
            {t.escolha}
          </option>
          {MODALIDADES_INFORMADAS.map((m) => (
            <option key={m} value={m}>
              {t.modalidades[m]}
            </option>
          ))}
        </Select>
      </Field>
      {modalidade === 'outra' ? (
        <p className="text-body-sm text-warning-700" data-testid="painel-modalidade-outra">
          {t.outra}
        </p>
      ) : null}
      <Fundamento id="painel-modalidade-fundamento" valor={fundamento} onChange={setFundamento} />
      <Falta falta={oQueFalta} estado={gravacao.estado} testId="painel-modalidade-falta" />
      <BotaoSalvar
        estado={gravacao.estado}
        recusa={gravacao.recusa}
        onSalvar={salvar}
        testId="painel-modalidade-salvar"
      />
    </div>
  );
}
